import { computed, DestroyRef, effect, inject, Service, signal, untracked } from '@angular/core';
import { form, max, min, required, validate } from '@angular/forms/signals';
import { finalize } from 'rxjs/operators';
import { OrderSide } from '@shared-types';
import { PriceFromHeadlineSide } from '@setting';
import { ORDER_FORM_CONFIGS } from '../constants/order-form.constants';
import { UserSettingEventConsumerService } from '../../../events/user-setting/user-setting-event-consumer.service';
import {
	DEFAULT_ORDER_VALIDITY,
	ORDER_SOURCE,
	SubmitOrderResponse,
	toOrderSideName
} from '../../../models/order-api.model';
import {
	IOrderActionButton,
	IOrderActionButtonType,
	OrderFormAccountContext,
	OrderFormData,
	OrderFormInputData,
	OrderFormType,
	OrderValidRangesType
} from '../../../models/order-form.model';
import { DEFAULT_KALA_USER_SETTING } from '../../../models/user-setting.model';
import { OrdersService } from '../../../services/orders.service';
import { ToastService } from '../../../services/toast.service';
import { WalletStore } from '../../wallet/data-access/wallet.store';

import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

const INITIAL_ORDER_FORM_DATA: OrderFormData = {
	isin: '',
	side: OrderSide.Buy,
	quantity: null,
	price: null
};

const INITIAL_VALID_RANGES: OrderValidRangesType = {
	volume: { min: 0, max: 0 },
	price: { min: 0, max: 0 }
};

const OVERSIZE_QUANTITY_FACTOR = 10;

@Service({ autoProvided: false })
export class OrderFormFacade {
	readonly orderFormModel = signal<OrderFormData>({ ...INITIAL_ORDER_FORM_DATA });
	readonly isEdit = signal(false);
	readonly isCopy = signal(false);
	readonly formType = signal(OrderFormType.DEPOSIT);
	readonly validRanges = signal<OrderValidRangesType>({ ...INITIAL_VALID_RANGES });
	/**
	 * The symbol's `tickSize` — the price grid the exchange accepts, anchored at 0, so a price
	 * is only submittable when it is a whole multiple of it. `0` is the absent marker and
	 * leaves the price unconstrained.
	 */
	readonly tickSize = signal(0);

	readonly orderTickType = computed(() => this.userSetting().order.tickType);

	readonly orderTick = computed(() => this.userSetting().order.tick);

	readonly priceFromHeadline = computed(() => this.userSetting().order.priceFromHeadline);

	readonly priceFromHeadlineSide = computed(() => this.userSetting().order.priceFromHeadlineSide);

	readonly orderBuyQuantity = computed(() => this.userSetting().order.buyQuantity);
	readonly orderSellQuantity = computed(() => this.userSetting().order.sellQuantity);

	/** Exchange id of the order under edit; null when submitting a new order. */
	readonly orderId = signal<number | null>(null);
	/** Price and quantity of the order under edit as loaded; an unchanged edit is not sent. */
	private readonly editBaseline = signal<Pick<OrderFormData, 'price' | 'quantity'> | null>(null);
	/** A submit or modify request is in flight. */
	readonly submitting = signal(false);

	private readonly ordersService = inject(OrdersService);
	private readonly toastService = inject(ToastService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly userSettingConsumer = inject(UserSettingEventConsumerService);
	private readonly walletStore = inject(WalletStore);
	private readonly userSetting = toSignal(this.userSettingConsumer.setting$, {
		initialValue: DEFAULT_KALA_USER_SETTING
	});

	readonly orderConfirmationEnabled = computed(() => this.userSetting().order.orderConfirmation);

	readonly quantityOverMax = computed(() => {
		const { volume } = this.validRanges();
		const quantity = this.orderFormModel().quantity;

		return volume.max > 0 && quantity !== null && quantity > volume.max;
	});

	readonly confirmBeforeAction = computed(() => this.orderConfirmationEnabled() || this.quantityOverMax());
	/**
	 * Order data as it stood when the confirm sheet opened. A locked price keeps
	 * tracking the best limit while the sheet is up — the input is expected to show
	 * that movement — but the order sent on confirm must be the one the summary
	 * showed, not whatever the price has drifted to since.
	 */
	private readonly confirmSnapshot = signal<OrderFormData | null>(null);
	/**
	 * Validation verdict for {@link confirmSnapshot}, taken at the moment it was pinned.
	 * The live form keeps validating against a price that is still moving, so the snapshot
	 * has to carry its own verdict — otherwise the order that is sent is never the one
	 * that was checked.
	 */
	private readonly confirmSnapshotError = signal<string[]>([]);
	/** «نگهداری اطلاعات سفارش پس از ارسال» — keeps the form filled after a successful submit. */
	readonly keepFormAfterSubmit = computed(() => this.userSetting().order.keepOrderFormAfterSubmit);
	readonly priceLocked = signal(false);

	/** Calculator panel visibility — shared so the price field can react to it. */
	readonly calculatorOpen = signal(false);
	/** Order amount (مبلغ) held by the calculator. Survives open/close of the panel. */
	readonly orderValue = signal(0);
	/** Set when the calculator's minimum action needs a price that has not been entered. */
	readonly calculatorPriceRequired = signal(false);

	/**
	 * The calculator is sizing an order that has no price yet, or its minimum action
	 * explicitly requested a price.
	 */
	readonly priceMissing = computed(() => {
		if (!this.calculatorOpen()) {
			return false;
		}
		const { price, quantity } = this.orderFormModel();
		if (price !== null && price !== undefined && price > 0) {
			return false;
		}
		return (quantity ?? 0) > 0 || this.orderValue() > 0 || this.calculatorPriceRequired();
	});

	readonly currentAsset = signal(0);
	readonly buyingPower = signal(0);
	readonly averageBuyPrice = signal(0);
	readonly buyCommissionRate = signal(0);
	readonly sellCommissionRate = signal(0);
	readonly bestBuyLimitPrice = signal(0);
	readonly bestSellLimitPrice = signal(0);

	/**
	 * The symbol's `lot`. Gated on itself, not on `maxOrderSize` — the two arrive as
	 * independent snapshot fields, so a symbol reporting one but not the other must keep
	 * whichever it does report.
	 */
	readonly quantityMin = computed(() => {
		const { volume } = this.validRanges();
		return volume.min > 0 ? volume.min : 1;
	});

	/**
	 * `maxOrderSize` off the symbol snapshot — a fill value for the field's «حداکثر» button,
	 * deliberately not a validation bound. The backend splits an oversize order into chunks of
	 * this size (350 against a max of 100 is accepted and becomes 4 orders), so blocking it
	 * here would reject an order the exchange takes.
	 *
	 * This is the exception, not the rule: every other bound in the schema blocks the submit
	 * (see `buildOrderPayload`). A quantity just past this ceiling is accepted because the
	 * backend rewrites an oversize order into a legal one, and only `OVERSIZE_QUANTITY_FACTOR`
	 * times past it does the schema call the order wrong; a price outside the thresholds or
	 * off the tick grid has no such rescue, so those are enforced before anything reaches the
	 * exchange.
	 */
	readonly quantityMax = computed(() => {
		const { volume } = this.validRanges();
		return volume.max > 0 ? volume.max : undefined;
	});

	private readonly formIsin = computed(() => this.orderFormModel().isin);

	private readonly quantityEdited = signal(false);

	readonly defaultQuantity = computed<number | null>(() => {
		if (this.orderFormModel().side === OrderSide.Sell) {
			const sellQuantity = this.orderSellQuantity();
			const held = this.currentAsset();

			return !Number.isFinite(sellQuantity) || held <= 0 ? null : Math.min(sellQuantity, held);
		}

		const buyQuantity = this.orderBuyQuantity();
		return Number.isFinite(buyQuantity) ? buyQuantity : null;
	});

	readonly form = form(this.orderFormModel, schemaPath => {
		required(schemaPath.isin, { message: 'نماد معتبر نیست' });
		required(schemaPath.side, { message: 'سمت سفارش اجباری است' });
		required(schemaPath.quantity, { message: 'تعداد باید بزرگتر از صفر باشد' });
		min(schemaPath.quantity, 1, { message: 'تعداد باید بزرگتر از صفر باشد' });
		validate(schemaPath.quantity, ({ value }) => {
			const { volume } = this.validRanges();
			const quantity = value();

			if (!quantity || quantity <= 0) {
				return null;
			}

			const belowMinimum = volume.min > 0 && quantity < volume.min;
			const farPastMaximum = volume.max > 0 && quantity > volume.max * OVERSIZE_QUANTITY_FACTOR;

			if (!belowMinimum && !farPastMaximum) {
				return null;
			}

			return { kind: 'volumeRange', message: 'حجم سفارش خارج از محدوده مجاز' };
		});
		// Quantity has no hard ceiling of its own — the backend chunks an oversize order — but
		// `OVERSIZE_QUANTITY_FACTOR` past the maximum is a wrong order, not an oversized one.
		// Price, unlike volume, is a hard band.
		required(schemaPath.price, { message: 'قیمت باید بزرگتر از صفر باشد' });
		min(schemaPath.price, 1, { message: 'قیمت باید بزرگتر از صفر باشد' });
		// Each bound gates on itself: `lowThreshold` and `highThreshold` are separate snapshot
		// fields, so a symbol carrying only one still gets that one enforced.
		min(
			schemaPath.price,
			() => {
				const { price } = this.validRanges();
				return price.min > 0 ? price.min : undefined;
			},
			{ message: 'قیمت خارج از محدوده مجاز' }
		);
		max(
			schemaPath.price,
			() => {
				const { price } = this.validRanges();
				return price.max > 0 ? price.max : undefined;
			},
			{ message: 'قیمت خارج از محدوده مجاز' }
		);

		validate(schemaPath.price, ({ value }) => {
			const tickSize = this.tickSize();
			const price = value();

			if (tickSize <= 0 || price === null || price % tickSize === 0) {
				return null;
			}

			return { kind: 'tickSize', message: `قیمت باید مضربی از ${tickSize} باشد` };
		});
	});

	readonly lockedBestPrice = computed(() => {
		const side = this.orderFormModel().side;
		return side === OrderSide.Buy ? this.bestSellLimitPrice() : this.bestBuyLimitPrice();
	});

	readonly actionButtons = computed<IOrderActionButton[]>(() => {
		const side = this.orderFormModel().side;
		if (side !== OrderSide.Buy && side !== OrderSide.Sell) {
			return [];
		}

		const formType = this.formType();
		const actions = ORDER_FORM_CONFIGS[formType].sideInfo[side].actions;
		const buttons: IOrderActionButton[] = [];

		if (this.isEdit()) {
			if (actions.EDIT) {
				buttons.push({ ...actions.EDIT, confirmBeforeAction: this.confirmBeforeAction() });
			}
			if (actions.CANCEL) {
				buttons.push({ ...actions.CANCEL });
			}
		} else {
			if (actions.SUBMIT) {
				buttons.push({ ...actions.SUBMIT, confirmBeforeAction: this.confirmBeforeAction() });
			}
			if (actions.CLOSE) {
				buttons.push({ ...actions.CLOSE });
			}
		}

		return buttons;
	});

	private seededIsin: null | string = null;

	private readonly onDismiss = signal<(() => void) | null>(null);

	constructor() {
		effect(() => {
			const isin = this.formIsin();
			const side = this.orderFormModel().side;
			const sameSide = this.priceFromHeadlineSide() === PriceFromHeadlineSide.Same;
			const useBuySide = side === OrderSide.Buy ? sameSide : !sameSide;
			const headlinePrice = useBuySide ? this.bestBuyLimitPrice() : this.bestSellLimitPrice();
			const seedFromHeadline = !!isin && !this.isEdit() && !this.isCopy() && this.priceFromHeadline();

			untracked(() => {
				if (!seedFromHeadline || this.seededIsin === isin) {
					return;
				}

				const price = this.orderFormModel().price;
				if (price !== null && price !== undefined) {
					this.seededIsin = isin;
					return;
				}

				if (headlinePrice <= 0) {
					return;
				}

				this.seededIsin = isin;
				this.pinLockedPrice(headlinePrice);
			});
		});

		effect(() => {
			if (!this.formIsin() || this.isEdit() || this.isCopy() || this.quantityEdited()) {
				return;
			}

			const quantity = this.defaultQuantity();
			untracked(() => {
				if (this.orderFormModel().quantity !== quantity) {
					this.orderFormModel.update(current => ({ ...current, quantity }));
				}
			});
		});

		effect(() => {
			if (!this.priceLocked()) {
				return;
			}

			const lockedPrice = this.lockedBestPrice();
			untracked(() => {
				if (lockedPrice > 0 && this.orderFormModel().price !== lockedPrice) {
					this.pinLockedPrice(lockedPrice);
				}
			});
		});
	}

	setOnDismiss(handler: () => void): void {
		this.onDismiss.set(handler);
	}

	/**
	 * Pins the order the user is about to confirm. Call when the confirm sheet opens
	 * and clear it once the sheet closes; the form itself keeps moving so a locked
	 * price still ticks in the input.
	 */
	captureConfirmSnapshot(): void {
		this.confirmSnapshot.set({ ...this.orderFormModel() });
		this.confirmSnapshotError.set(this.validationErrors());
	}

	notifyIfEditUnchanged(): boolean {
		const baseline = this.editBaseline();
		const { price, quantity } = this.confirmSnapshot() ?? this.getFormValue();
		if (!baseline || baseline.price !== price || baseline.quantity !== quantity) {
			return false;
		}

		this.toastService.info('تغییری برای اعمال وجود ندارد');
		return true;
	}

	/** Drops the pinned order, so later actions read live form values again. */
	clearConfirmSnapshot(): void {
		this.confirmSnapshot.set(null);
		this.confirmSnapshotError.set([]);
	}

	/** Holding, buying-power and commission figures the calculator and summary read. */
	setAccountContext(context: OrderFormAccountContext): void {
		this.currentAsset.set(context.currentAsset);
		this.buyingPower.set(context.buyingPower);
		this.averageBuyPrice.set(context.averageBuyPrice);
		this.buyCommissionRate.set(context.buyCommissionRate);
		this.sellCommissionRate.set(context.sellCommissionRate);
	}

	/** Price band and volume limits the order is validated against. */
	setValidRanges(ranges: OrderValidRangesType): void {
		this.validRanges.set({ ...ranges });
	}

	/** The price grid the order is validated against; anything not positive means no grid. */
	setTickSize(tickSize: number): void {
		this.tickSize.set(tickSize > 0 ? tickSize : 0);
	}

	setBestLimitPrices(bestBuyLimitPrice: number, bestSellLimitPrice: number): void {
		if (bestBuyLimitPrice > 0) {
			this.bestBuyLimitPrice.set(bestBuyLimitPrice);
		}
		if (bestSellLimitPrice > 0) {
			this.bestSellLimitPrice.set(bestSellLimitPrice);
		}
	}

	togglePriceLock(): void {
		const next = !this.priceLocked();
		this.priceLocked.set(next);
		if (next) {
			const lockedPrice = this.lockedBestPrice();
			if (lockedPrice > 0) {
				this.pinLockedPrice(lockedPrice);
			}
		}
	}

	setPriceLock(locked: boolean): void {
		this.priceLocked.set(locked);
		if (locked) {
			const lockedPrice = this.lockedBestPrice();
			if (lockedPrice > 0) {
				this.pinLockedPrice(lockedPrice);
			}
		}
	}

	private pinLockedPrice(price: number): void {
		this.orderFormModel.update(current => ({ ...current, price }));
	}

	setFormData(data: OrderFormInputData): void {
		if (data.isEdit === true || data.isCopy === true) {
			this.setPriceLock(false);
		}

		const input = this.priceLocked() ? this.withoutLockedPair(data) : data;
		const previousPrice = this.orderFormModel().price;
		const merged = this.mergeFormData(input);

		this.orderFormModel.set(merged);

		// A cleared price takes the order amount with it — nothing is derivable without a price.
		if (!merged.price && previousPrice) {
			this.orderValue.set(0);
		}
		if (merged.price !== null && merged.price !== undefined && merged.price > 0) {
			this.calculatorPriceRequired.set(false);
		}

		if (data.quantity !== undefined) {
			this.quantityEdited.set(true);
		}

		if (data.isEdit !== undefined) {
			this.isEdit.set(data.isEdit);
			this.editBaseline.set(data.isEdit ? { price: merged.price, quantity: merged.quantity } : null);
		}
		if (data.isCopy !== undefined) {
			this.isCopy.set(data.isCopy);
		}
		if (data.orderId !== undefined) {
			this.orderId.set(data.orderId);
		}
		if (data.formType !== undefined) {
			this.formType.set(data.formType);
		}
		if (data.validRanges !== undefined) {
			this.validRanges.set(data.validRanges);
		}
		if (data.bestBuyLimitPrice !== undefined || data.bestSellLimitPrice !== undefined) {
			this.setBestLimitPrices(data.bestBuyLimitPrice ?? 0, data.bestSellLimitPrice ?? 0);
		}
	}

	resetForm(): void {
		this.orderFormModel.set({ ...INITIAL_ORDER_FORM_DATA });
		this.form().reset();
		this.isEdit.set(false);
		this.isCopy.set(false);
		this.quantityEdited.set(false);
		this.seededIsin = null;
		this.formType.set(OrderFormType.DEPOSIT);
		this.priceLocked.set(false);
		this.calculatorOpen.set(false);
		this.orderValue.set(0);
		this.calculatorPriceRequired.set(false);
		this.confirmSnapshot.set(null);
		this.confirmSnapshotError.set([]);
		this.validRanges.set({ ...INITIAL_VALID_RANGES });
		this.tickSize.set(0);
		this.orderId.set(null);
		this.editBaseline.set(null);
		this.submitting.set(false);
	}

	getFormValue(): OrderFormData {
		return this.orderFormModel();
	}

	handleAction(action: IOrderActionButtonType): void {
		switch (action) {
			case 'SUBMIT':
				this.submit();
				break;
			case 'EDIT':
				this.edit();
				break;
			case 'CANCEL':
				this.cancel();
				break;
			case 'CLOSE':
				this.close();
				break;
		}
	}

	private submit(): void {
		const payload = this.buildOrderPayload();
		if (!payload) {
			return;
		}

		this.submitting.set(true);
		this.ordersService
			.submitOrder({
				symbolIsin: payload.isin,
				side: toOrderSideName(payload.side),
				price: payload.price,
				volume: payload.quantity,
				validity: DEFAULT_ORDER_VALIDITY,
				validityDate: null,
				source: ORDER_SOURCE
			})
			.pipe(
				finalize(() => this.submitting.set(false)),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe({
				next: results => this.handleSubmitResults(results),
				error: (error: unknown) => {
					const errorMessage =
						error instanceof Error && error.message ? error.message : 'ثبت سفارش انجام نشد';
					this.failRequest(errorMessage);
				}
			});
	}

	private edit(): void {
		const orderId = this.orderId();

		// `Order.id` is a string, so a non-numeric id arrives here as NaN — which is
		// neither null nor a usable route segment.
		if (orderId === null || !Number.isFinite(orderId)) {
			this.failRequest('شناسه سفارش در دسترس نیست');
			return;
		}

		const payload = this.buildOrderPayload();

		if (!payload) {
			return;
		}

		if (this.notifyIfEditUnchanged()) {
			return;
		}

		this.submitting.set(true);
		this.ordersService
			.modifyOrder(orderId, {
				price: payload.price,
				volume: payload.quantity,
				validity: DEFAULT_ORDER_VALIDITY,
				validityDate: null,
				source: ORDER_SOURCE
			})
			.pipe(
				finalize(() => this.submitting.set(false)),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe({
				next: () => this.finishSuccessfulRequest(),
				error: (error: unknown) => {
					const errorMessage =
						error instanceof Error && error.message ? error.message : 'ویرایش سفارش انجام نشد';
					this.failRequest(errorMessage);
				}
			});
	}

	/**
	 * First schema message across the whole field tree, or null when the live form is valid.
	 * Read when the order is actually sent — the confirm sheet opens either way, so the message
	 * lands on the submit the user committed to rather than on the button that raises the sheet.
	 */
	readonly validationErrors = computed<string[]>(() => {
		const formState = this.form();
		if (!formState.invalid()) {
			return [];
		}

		const messages = [
			...new Set(formState.errorSummary().flatMap(error => (error.message ? [error.message] : [])))
		];

		return messages.length ? messages : ['اطلاعات سفارش معتبر نیست'];
	});

	/**
	 * Single exit for every failure. A toast is the whole report: the form has nowhere to render a
	 * message — `ui-input` carries an error border but no message slot — and nothing else reads
	 * failures back off the facade.
	 */
	private failRequest(...messages: string[]): void {
		for (const message of messages) {
			this.toastService.error(message);
		}
	}

	/**
	 * Narrows the nullable price/quantity so callers get a payload that is safe to send.
	 * Always returns null with a toast raised, never null in silence.
	 *
	 * Values come from the confirm snapshot when one is pinned, so a locked price that
	 * moved while the sheet was open does not change what gets sent — and the verdict
	 * used is the one taken when that snapshot was pinned, so the order that is checked
	 * is always the order that is sent.
	 *
	 * A failing verdict blocks the send: the message is raised and nothing reaches the
	 * exchange, so an order the form already knows is invalid is never submitted.
	 */
	private buildOrderPayload(): { isin: string; side: OrderSide; price: number; quantity: number } | null {
		if (this.submitting()) {
			return null;
		}

		const snapshot = this.confirmSnapshot();
		const validationErrors = snapshot ? this.confirmSnapshotError() : this.validationErrors();
		if (validationErrors.length) {
			this.failRequest(...validationErrors);
			return null;
		}

		const data = snapshot ?? this.getFormValue();
		if (data.price === null || data.quantity === null) {
			this.failRequest('اطلاعات سفارش معتبر نیست');
			return null;
		}

		return { isin: data.isin, side: data.side, price: data.price, quantity: data.quantity };
	}

	/**
	 * The exchange creates one order per entry and can accept some while rejecting
	 * others, so a single rejection keeps the form open with its message.
	 */
	private handleSubmitResults(results: SubmitOrderResponse[]): void {
		// No entries means no order was created, however the envelope reported itself —
		// treating that as success would clear the form and claim an order that does not exist.
		if (results.length === 0) {
			this.failRequest('ثبت سفارش انجام نشد');
			return;
		}

		const rejected = results.find(result => !result.isAccepted);
		if (rejected) {
			this.failRequest(rejected.message ?? 'ثبت سفارش انجام نشد');
			return;
		}

		this.finishSuccessfulRequest();
	}

	/**
	 * An edit always closes — the order it targets no longer needs a form. A new submit
	 * closes only when the user did not ask to keep the form: the entered order data stays
	 * and the sheet stays up when the setting is on, and is cleared and dismissed otherwise.
	 *
	 * Same rule as desktop, where `closeActionEvent$` in
	 * `libs/order-form-tabular/.../base-order-form.facade.ts` closes a new order's form when
	 * the "keep form after submit" setting is off.
	 */
	private finishSuccessfulRequest(): void {
		// An accepted order has already moved the customer's money, so قدرت خرید on the
		// footer's bar and in this very summary is out of date the moment the toast shows.
		// The server announces the same thing over `getremain`, but not on any promised
		// schedule — and this is the one change the trader watches for, so it is asked for
		// here rather than waited on. Fire-and-forget: a failed refresh leaves the previous
		// figure standing, which is the store's behaviour everywhere else.
		this.walletStore.load();

		if (this.isEdit()) {
			this.toastService.info('درخواست ویرایش ارسال شد');
			this.resetForm();
			this.onDismiss()?.();
			return;
		}

		this.toastService.info(`سفارش ${this.orderFormModel().side === OrderSide.Buy ? 'خرید' : 'فروش'} ارسال شد`);

		if (!this.keepFormAfterSubmit()) {
			this.form().reset({ ...this.getFormValue(), quantity: null, price: null });
			this.clearConfirmSnapshot();
			this.onDismiss()?.();
			return;
		}

		this.seededIsin = null;
	}

	private cancel(): void {
		this.resetForm();
		this.onDismiss()?.();
	}

	private close(): void {
		this.resetForm();
		this.onDismiss()?.();
	}

	private withoutLockedPair(data: OrderFormInputData): OrderFormInputData {
		if (data.price === undefined || data.price === this.orderFormModel().price) {
			return data;
		}

		const rest = { ...data };
		delete rest.price;
		delete rest.quantity;
		return rest;
	}

	private mergeFormData(data: OrderFormInputData): OrderFormData {
		const current = this.getFormValue();

		return {
			isin: data.isin ?? current.isin,
			side: data.side ?? current.side,
			quantity: data.quantity !== undefined ? data.quantity : current.quantity,
			price: data.price !== undefined ? data.price : current.price
		};
	}
}
